import type { AuthenticatedUser, LoginResponse } from '@minisoc/contracts';

/**
 * Persistance de la session côté navigateur.
 *
 * Choix assumé : `localStorage`. Un cookie `HttpOnly` serait plus résistant au
 * vol de token par XSS, mais impose un domaine partagé entre l'API et les deux
 * fronts, ce que l'architecture du service central ne garantit pas (§3.2.1 :
 * VM Backend API et VM Dashboard/Backoffice distinctes). Le risque est réduit
 * par un access token court (15 min) et un refresh token rotatif révoqué dès
 * son premier usage.
 *
 * À revoir si les fronts et l'API passent derrière un même reverse proxy.
 */

const STORAGE_PREFIX = 'minisoc';

export interface StoredSession {
  readonly accessToken: string;
  readonly refreshToken: string;
  readonly user: AuthenticatedUser;
  /** Epoch en millisecondes d'expiration de l'access token. */
  readonly expiresAt: number;
}

export class SessionStore {
  private readonly key: string;
  private listeners = new Set<(session: StoredSession | null) => void>();

  /** `namespace` sépare les sessions du backoffice et du dashboard sur localhost. */
  constructor(namespace: string) {
    this.key = `${STORAGE_PREFIX}:${namespace}:session`;
  }

  read(): StoredSession | null {
    try {
      const raw = localStorage.getItem(this.key);
      if (!raw) return null;

      const parsed = JSON.parse(raw) as StoredSession;
      if (!parsed.accessToken || !parsed.refreshToken || !parsed.user) return null;

      return parsed;
    } catch {
      // Session corrompue (mise à jour de format, écriture partielle) : on la
      // traite comme absente plutôt que de bloquer l'application au démarrage.
      return null;
    }
  }

  write(login: LoginResponse): StoredSession {
    const session: StoredSession = {
      accessToken: login.accessToken,
      refreshToken: login.refreshToken,
      user: login.user,
      expiresAt: Date.now() + login.expiresIn * 1000,
    };

    localStorage.setItem(this.key, JSON.stringify(session));
    this.notify(session);
    return session;
  }

  /** Met à jour les jetons après rotation, en conservant le profil utilisateur. */
  updateTokens(tokens: { accessToken: string; refreshToken: string; expiresIn: number }): void {
    const current = this.read();
    if (!current) return;

    const session: StoredSession = {
      ...current,
      accessToken: tokens.accessToken,
      refreshToken: tokens.refreshToken,
      expiresAt: Date.now() + tokens.expiresIn * 1000,
    };

    localStorage.setItem(this.key, JSON.stringify(session));
    this.notify(session);
  }

  clear(): void {
    localStorage.removeItem(this.key);
    this.notify(null);
  }

  subscribe(listener: (session: StoredSession | null) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notify(session: StoredSession | null): void {
    for (const listener of this.listeners) listener(session);
  }
}
